import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { OrchestratorService } from '../../../services/orchestrator/orchestrator.service';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { InfoDialogComponent } from './info-dialog/info-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ScenarioCategory,
  ScenarioItem,
} from '../../../services/result-data/result-data.service';
import { MatDividerModule } from '@angular/material/divider';
import { ResetConfirmationDialogComponent } from './reset-confirmation-dialog/reset-confirmation-dialog.component';
import { PercentInputDirective } from '../../../directives/percent-input.directive';
import { IntroStateService } from '../../../services/intro-state/intro-state.service';

@Component({
  selector: 'app-scenario',
  imports: [
    ReactiveFormsModule,
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatButtonModule,
    MatExpansionModule,
    MatIconModule,
    MatDividerModule,
    PercentInputDirective,
    MatButtonModule,
  ],
  templateUrl: './scenario.component.html',
  styleUrl: './scenario.component.scss',
})
export class ScenarioComponent implements OnInit, OnChanges {
  form: FormGroup;
  expandedPanels: Record<string, boolean> = {};

  @Input() categories: ScenarioCategory[] = [];

  private _subscriptions: Subscription[] = [];

  constructor(
    private readonly _fb: FormBuilder,
    private readonly _orchestrator: OrchestratorService,
    private readonly _dialog: MatDialog,
    private readonly _snackBar: MatSnackBar,
    private readonly _introStateService: IntroStateService
  ) {
    this.form = this._fb.group({});
  }

  ngOnInit() {
    this.categories.forEach((cat, i) => {
      this._handleControls(cat);
      this._handleExpansionPanels(cat, i);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['categories']) return;

    this.categories.forEach((cat) => {
      cat.items.forEach((item) => {
        const controlName = this.controlKey(cat.name, item.name);
        const control = this.form.get([controlName]);

        if (!control || control.value === item.defaultValue) return;

        control.setValue(item.defaultValue, { emitEvent: false });
      });
    });
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach((sub) => sub.unsubscribe());
  }

  onUserFinishedTyping(): void {
    this._orchestrator.generateScenario(this.form);
  }

  onClickReset(): void {
    this._dialog.open(ResetConfirmationDialogComponent);
  }

  onClickInfo() {
    this._introStateService.activate();
  }

  onClickExport(): void {
    console.log('export');
  }

  onClickOpenInfo(
    categoryName: string,
    itemName: string,
    comment: string
  ): void {
    this._dialog.open(InfoDialogComponent, {
      data: { categoryName, itemName, comment },
    });
  }

  openSnackBar(message: string, action: string) {
    this._snackBar.open(message, action);
  }

  // Required to avoid input unfocused
  trackByItem = (_: number, item: ScenarioItem) => item.name;

  controlKey(catName: string | undefined, itemName: string): string {
    return this._controlKey(catName, itemName);
  }

  private _handleControls(cat: ScenarioCategory) {
    cat.items.forEach((item) => {
      const controlName = this._controlKey(cat.name, item.name);
      const control = new FormControl(
        {
          value: item.defaultValue,
          disabled: !item.modifiable,
        },
        Validators.required
      );

      const controlSub = control.valueChanges
        .pipe(
          debounceTime(500), // wait 500ms after last keystroke
          distinctUntilChanged() // only emit when value changes
        )
        .subscribe(() => {
          this.onUserFinishedTyping();
        });
      this._subscriptions.push(controlSub);
      this.form.addControl(controlName, control);
    });
  }

  private _handleExpansionPanels(cat: ScenarioCategory, i: number) {
    this.expandedPanels[cat.name] = i == 0;
  }

  private _controlKey(catName: string | undefined, itemName: string): string {
    return `${catName ?? ''}::${itemName}`.trim();
  }
}

